import type { Dispatch } from 'redux'
import { Cart, ResponseError, nativeAppHeaders } from '@ynap/api-utils'
import { timeout, delay } from '@ynap/promise-utils'
import { notifyBasketChanged, getLocale } from '@ynap/cart-state'
import { getWatsonConfig, getTimeout, getDelay } from '@ynap/cart-state/src/selectors/getConfig'
import { StoreState } from 'app/state/types'
import { OrderItem, ShoppingCart } from 'app/types/common-types'
import { UNDO_REMOVE_ITEM_REQUEST, UNDO_REMOVE_ITEM_SUCCESS, UNDO_REMOVE_ITEM_ERROR } from '../constants/ActionTypes'
import { getAdditionalCheckoutHeaders } from '../utils/checkoutActionUtils'

interface State extends StoreState {
    cart: ShoppingCart & {
        orderItemIncludingRemoved: OrderItem[]
    }
}

type UndoRemoveItemResponseData = {
    orderId: string
    orderItem: { orderItemId: number }[]
    shoppingCart: ShoppingCart
    version: string
    resourceId: string
}

export default function undoRemoveItem({ partNumber, reservationId }: { partNumber: string; reservationId: string }) {
    return (dispatch: Dispatch, getState: () => State, fetch: WindowOrWorkerGlobalScope['fetch']) => {
        const state = getState()
        const watsonConfig = getWatsonConfig(state)
        const { langId } = getLocale(state)
        const { orderItemIncludingRemoved } = state.cart // TODO: Should use shared getCart selector?
        const additionalHeaders = {
            ...nativeAppHeaders(state.deviceType),
            ...getAdditionalCheckoutHeaders(state),
        }

        const orderItemToUndoRemove = orderItemIncludingRemoved.find((item) => item.partNumber === partNumber)

        dispatch(undoRemoveItemRequest(partNumber))

        const request = Cart.addToCart({
            partNumber,
            quantity: orderItemToUndoRemove?.quantity,
            ...watsonConfig,
            includeShoppingCart: true,
            langId,
            reservationId,
            additionalHeaders,
        })

        return delay(timeout(fetch(request), getTimeout(state)), getDelay(state))
            .then((response: Response) => {
                if (!response.ok) {
                    throw new ResponseError(response)
                }
                return response.json()
            })
            .then((data: UndoRemoveItemResponseData) => {
                dispatch(undoRemoveItemSuccess(partNumber, orderItemToUndoRemove?.orderItemId, data))
                notifyBasketChanged()
            })
            .catch((error: unknown) => {
                dispatch(undoRemoveItemError(partNumber, error))
                throw error
            })
    }
}

function undoRemoveItemRequest(partNumber: string) {
    return {
        type: UNDO_REMOVE_ITEM_REQUEST,
        partNumber,
    }
}

function undoRemoveItemSuccess(partNumber: string, orderItemId: number | undefined, payload: UndoRemoveItemResponseData) {
    return {
        type: UNDO_REMOVE_ITEM_SUCCESS,
        partNumber,
        orderItemId,
        payload,
    }
}

function undoRemoveItemError(partNumber: string, error: unknown) {
    return {
        type: UNDO_REMOVE_ITEM_ERROR,
        partNumber,
        error,
    }
}
